import { Routes, Route, Navigate } from "react-router-dom";

import { BILLING_RETURN_PATH } from "@/lib/auth/auth-paths";

import { GuestGuard } from "@/components/layout/guest-guard";
import { AuthLayout } from "@/components/layouts/AuthLayout";
import { MainLayout } from "@/components/layouts/MainLayout";
import { OnboardingLayout } from "@/components/layouts/OnboardingLayout";

import { LoginPage } from "@/pages/auth/LoginPage";
import { VerifyEmailPage } from "@/pages/auth/VerifyEmailPage";
import { LogoutPage } from "@/pages/auth/LogoutPage";
import { LogoutSuccessPage } from "@/pages/auth/LogoutSuccessPage";

import { GettingStartedPage } from "@/pages/main/GettingStartedPage";
import { DevicesPage } from "@/pages/main/devices/DevicesPage";
import { DeviceDetailPage } from "@/pages/main/devices/DeviceDetailPage";
import { NotificationsPage } from "@/pages/main/notifications/NotificationsPage";
import { NewNotificationPage } from "@/pages/main/notifications/NewNotificationPage";
import { NotificationDetailPage } from "@/pages/main/notifications/NotificationDetailPage";
import { NewServicePage } from "@/pages/main/services/NewServicePage";
import { EditServicePage } from "@/pages/main/services/EditServicePage";
import { ServiceSwitchPage } from "@/pages/main/services/ServiceSwitchPage";
import { WidgetsPage } from "@/pages/main/widgets/WidgetsPage";
import { WidgetDetailPage } from "@/pages/main/widgets/WidgetDetailPage";
import { DataSourcesPage } from "@/pages/main/widgets/DataSourcesPage";
import { DataSourceDetailPage } from "@/pages/main/widgets/DataSourceDetailPage";
import { NewDataSourcePage } from "@/pages/main/widgets/NewDataSourcePage";
import { EditDataSourcePage } from "@/pages/main/widgets/EditDataSourcePage";
import { NewWidgetPage } from "@/pages/main/widgets/NewWidgetPage";
import { EditWidgetPage } from "@/pages/main/widgets/EditWidgetPage";
import { AccountSettingsPage } from "@/pages/main/settings/AccountSettingsPage";
import { ApiKeysPage } from "@/pages/main/settings/ApiKeysPage";
import { NewApiKeyPage } from "@/pages/main/settings/NewApiKeyPage";
import { ApiKeyDetailPage } from "@/pages/main/settings/ApiKeyDetailPage";
import { EditApiKeyPage } from "@/pages/main/settings/EditApiKeyPage";

import { OnboardingPage } from "@/pages/onboarding/OnboardingPage";
import { OnboardingNotificationPage } from "@/pages/onboarding/OnboardingNotificationPage";
import { OnboardingDownloadPage } from "@/pages/onboarding/OnboardingDownloadPage";
import { OnboardingWidgetPage } from "@/pages/onboarding/OnboardingWidgetPage";

import { OnboardingDonePage } from "@/pages/public/onboarding-done-page";
import { PricingPage } from "@/pages/public/pricing-page";
import { BillingReturnPage } from "@/pages/public/billing-return-page";

import { InternalErrorPage } from "@/pages/InternalErrorPage";
import { NotFoundPage } from "@/pages/NotFoundPage";

export function AppRoutes() {
  return (
    <Routes>
      <Route element={<AuthLayout />}>
        <Route element={<GuestGuard />}>
          <Route path="/login" element={<LoginPage />} />
        </Route>

        {/* Verify consumes the magic-link token as a side effect and navigates
            on its own, so it must run even with a stale session present (a
            re-clicked link in an already-signed-in browser, or a dev seeded via
            VITE_DEV_AUTH_JWT). NOT guest-guarded. */}
        <Route path="/auth/verify" element={<VerifyEmailPage />} />

        {/* Must stay reachable while authenticated. */}
        <Route path="/logout" element={<LogoutPage />} />
        <Route path="/logout-success" element={<LogoutSuccessPage />} />
      </Route>

      <Route element={<MainLayout />}>
        {/* The dashboard opens on Getting started, which has no menu item:
            the card under the service picker leads to it. */}
        <Route path="/" element={<Navigate to="/getting-started" replace />} />
        <Route path="/getting-started" element={<GettingStartedPage />} />

        <Route path="/devices" element={<DevicesPage />} />
        <Route path="/devices/:id" element={<DeviceDetailPage />} />

        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/notifications/new" element={<NewNotificationPage />} />
        <Route path="/notifications/:id" element={<NotificationDetailPage />} />

        <Route path="/services/new" element={<NewServicePage />} />
        <Route path="/services/:id" element={<ServiceSwitchPage />} />
        <Route path="/services/:id/edit" element={<EditServicePage />} />

        <Route path="/widgets" element={<WidgetsPage />} />
        <Route path="/widgets/data-sources" element={<DataSourcesPage />} />

        <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
        <Route path="/settings/account" element={<AccountSettingsPage />} />
        <Route path="/settings/api-keys" element={<ApiKeysPage />} />
        <Route path="/settings/api-keys/new" element={<NewApiKeyPage />} />
        <Route path="/settings/api-keys/:id" element={<ApiKeyDetailPage />} />
        <Route path="/settings/api-keys/:id/edit" element={<EditApiKeyPage />} />

        <Route path="/widgets/new" element={<NewWidgetPage />} />
        <Route path="/widgets/data-sources/new" element={<NewDataSourcePage />} />
        <Route path="/widgets/:id" element={<WidgetDetailPage />} />
        <Route path="/widgets/:id/edit" element={<EditWidgetPage />} />
        <Route path="/widgets/data-sources/:id" element={<DataSourceDetailPage />} />
        <Route path="/widgets/data-sources/:id/edit" element={<EditDataSourcePage />} />
      </Route>

      <Route element={<OnboardingLayout />}>
        <Route path="/onboarding" element={<OnboardingPage />} />
        <Route path="/onboarding/notification" element={<OnboardingNotificationPage />} />
        <Route path="/onboarding/download" element={<OnboardingDownloadPage />} />
        <Route path="/onboarding/widget" element={<OnboardingWidgetPage />} />
      </Route>

      <Route path="/onboarding-done/:serviceId?" element={<OnboardingDonePage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path={BILLING_RETURN_PATH} element={<BillingReturnPage />} />
      <Route path="/internal-error" element={<InternalErrorPage />} />

      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}
